/**
 * useChatContext Hook
 *
 * Detects the current page from the router and extracts context metadata
 * (page name, project_id, api_request_id) to include in chat requests.
 * Also gathers rich page data from the global context store populated
 * by page components via usePageDataCollector.
 *
 * Requirements: 11.1, 11.2, 11.3, 11.4
 */

import { useMemo } from 'react';
import { getPageData } from './usePageDataCollector';

/** Map route prefixes to page identifiers */
const PAGE_MAP = [
  { prefix: '/inputs/load', page: 'Load Test' },
  { prefix: '/inputs', page: 'Workspace' },
  // Matches /orchestration-load-result/*. The standalone /orchestration page
  // was retired, but this prefix still classifies the results route — do not
  // remove it as dead just because ROUTES.ORCHESTRATION is gone.
  { prefix: '/orchestration', page: 'Orchestration' },
  { prefix: '/mock-servers', page: 'Mock Servers' },
  { prefix: '/environment', page: 'Environments' },
  { prefix: '/scheduled-test-plans', page: 'Test Plans' },
  { prefix: '/test-logs', page: 'Test Logs' },
  { prefix: '/dashboard', page: 'Dashboard' },
  { prefix: '/admin', page: 'Admin' },
  { prefix: '/workflow', page: 'Workflow' },
  { prefix: '/ai-generate', page: 'AI Generate' },
];

/**
 * Detect the current page from a pathname.
 * @param {string} pathname
 * @returns {string} page identifier
 */
function detectPage(pathname) {
  for (const { prefix, page } of PAGE_MAP) {
    if (pathname.startsWith(prefix)) return page;
  }
  return 'Unknown';
}

/**
 * Extract project_id and api_request_id from URL search params or
 * sessionStorage (common pattern in the existing codebase).
 */
function extractResourceIds() {
  const params = new URLSearchParams(window.location.search);
  const projectId = params.get('project_id') || params.get('projectId') || null;
  const apiRequestId = params.get('api_request_id') || params.get('apiRequestId') || null;
  return {
    project_id: projectId ? Number(projectId) : null,
    api_request_id: apiRequestId ? Number(apiRequestId) : null,
  };
}

/**
 * Build rich page context by reading from the global page data store.
 * Each page component writes its data via usePageDataCollector / setPageData.
 *
 * @param {string} page - Current page identifier
 * @returns {Object} Rich context fields (api_request, api_response, orchestration, load_test)
 */
function buildRichContext(page) {
  const pageData = getPageData();
  const rich = {};

  // API request/response context — available from any page since execution results persist
  if (pageData.request) {
    rich.api_request = {
      method: pageData.request.method || null,
      url: pageData.request.url || null,
      headers: pageData.request.headers || null,
      auth_type: pageData.request.auth_type || null,
      query_params: pageData.request.query_params || null,
      request_body: pageData.request.request_body || null,
    };
  }

  if (pageData.response) {
    rich.api_response = {
      status_code: pageData.response.status_code ?? null,
      response_body: pageData.response.response_body || null,
      response_headers: pageData.response.response_headers || null,
      response_time_ms: pageData.response.response_time_ms ?? null,
      error_message: pageData.response.error_message || null,
    };
  }

  // Orchestration context — available from any page since execution results persist
  if (pageData.orchestration) {
    rich.orchestration = {
      name: pageData.orchestration.name || null,
      step_count: pageData.orchestration.step_count ?? null,
      failed_steps: pageData.orchestration.failed_steps || null,
      environment: pageData.orchestration.environment || null,
      dataset: pageData.orchestration.dataset || null,
      step_results: pageData.orchestration.step_results || null,
    };
  }

  // Load Test context — available from any page since results persist
  if (pageData.loadTest) {
    rich.load_test = {
      request_name: pageData.loadTest.request_name || null,
      concurrency: pageData.loadTest.concurrency ?? null,
      p50: pageData.loadTest.p50 ?? null,
      p90: pageData.loadTest.p90 ?? null,
      p95: pageData.loadTest.p95 ?? null,
      p99: pageData.loadTest.p99 ?? null,
      avg_response_time_ms: pageData.loadTest.avg_response_time_ms ?? null,
      min_response_time_ms: pageData.loadTest.min_response_time_ms ?? null,
      max_response_time_ms: pageData.loadTest.max_response_time_ms ?? null,
      throughput: pageData.loadTest.throughput ?? null,
      error_rate: pageData.loadTest.error_rate ?? null,
      total_requests: pageData.loadTest.total_requests ?? null,
      successful_requests: pageData.loadTest.successful_requests ?? null,
      failed_requests: pageData.loadTest.failed_requests ?? null,
      total_duration_seconds: pageData.loadTest.total_duration_seconds ?? null,
    };
  }

  // Advanced Orchestration context (available on Orchestration page when in advanced mode)
  if (pageData.advancedOrchestration) {
    rich.advanced_orchestration = {
      name: pageData.advancedOrchestration.name || null,
      overall_status: pageData.advancedOrchestration.overall_status || null,
      total_duration_ms: pageData.advancedOrchestration.total_duration_ms ?? null,
      nodes_executed: pageData.advancedOrchestration.nodes_executed ?? null,
      nodes_failed: pageData.advancedOrchestration.nodes_failed ?? null,
      node_results: pageData.advancedOrchestration.node_results || null,
      failed_nodes: pageData.advancedOrchestration.failed_nodes || null,
    };
  }

  // Mock Servers page — include in orchestration-like structure for now
  if (page === 'Mock Servers' && pageData.mockServer) {
    rich.mock_server = {
      name: pageData.mockServer.name || null,
      url: pageData.mockServer.url || null,
      status: pageData.mockServer.status || null,
      route_count: pageData.mockServer.route_count ?? null,
      last_request_log: pageData.mockServer.last_request_log || null,
    };
  }

  // Environment / project name from any page
  if (pageData.environment) {
    rich.environment_name = pageData.environment.name || null;
  }
  if (pageData.project) {
    rich.project_name = pageData.project.name || null;
  }

  return rich;
}

/**
 * @returns {Object} context metadata for chat requests
 */
export function useChatContext() {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';

  const context = useMemo(() => {
    const page = detectPage(pathname);
    const { project_id, api_request_id } = extractResourceIds();

    // Build rich context from page data store
    const richContext = buildRichContext(page);

    return {
      page,
      project_id,
      api_request_id,
      ...richContext,
    };
  }, [pathname]);

  return context;
}

export default useChatContext;
